import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable, of, Subject, throwError, combineLatest } from 'rxjs';
import { map, takeUntil, tap, switchMap, catchError, debounceTime, filter, take, pairwise, startWith } from 'rxjs/operators';
import { UserProfileService } from '../services/user-profile.service';
import { TeamMetricsService } from '../services/team-metrics.service';
import { TeamOwnerMinorEntry } from '../models/team-owner-minor-entry';
import { AdoUserProfile } from '../models/ado-user-profile';
import { EnvironmentService } from '../services/environment.service';
import { Environment } from '../models/environment';
import { AdoAccount } from '../models/ado-account';
import { TimeRangeOption } from '../models/time-range-option';
import { CalculationLogic, DEFAULT_CALCULATION_LOGIC } from '../models/team-metrics';

@Component({
  selector: 'app-team-metrics',
  standalone: false,
  template: `
    <div class="metrics-container">
      <h1 class="metricsHeader">Team Metrics</h1>
      <h2 class="date-display-header">{{ currentDateRangeDisplay }}</h2>
      <div class="filter-bar">
        <div class="filter-controls">
          <form [formGroup]="filterForm">
            <mat-form-field subscriptSizing="dynamic" appearance="fill">
              <mat-label>Time Range</mat-label>
              <mat-select formControlName="timeRange">
                @for (range of timeRangeOptions; track range) {
                  <mat-option [value]="range.value">
                    {{range.display}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
    
            @if (filterForm.get('timeRange')?.value === 'custom') {
              <mat-form-field subscriptSizing="dynamic" appearance="fill">
                <mat-label>Enter a date range</mat-label>
                <mat-date-range-input [formGroup]="filterForm" [rangePicker]="picker" [max]="maxDate">
                  <input matStartDate formControlName="customStartDate" placeholder="Start date">
                  <input matEndDate formControlName="customEndDate" placeholder="End date">
                </mat-date-range-input>
                <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-date-range-picker #picker></mat-date-range-picker>
              </mat-form-field>
            }
    
            <mat-form-field subscriptSizing="dynamic" appearance="fill">
              <mat-label>Team</mat-label>
              <mat-select formControlName="team">
                @for (team of teams$ | async; track team) {
                  <mat-option [value]="team">
                    {{team}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
    
            <mat-form-field subscriptSizing="dynamic" appearance="fill">
              <mat-label>Calc Method</mat-label>
              <mat-select formControlName="calculationLogic">
                @for (option of calculationLogicOptions; track option) {
                  <mat-option [value]="option">
                    {{ option }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </form>
        </div>
        <!-- Date range container moved above filter-bar -->
      </div>
      <div class="metrics-cards-grid">
        <div class="kpi-row">
          <app-change-fail-rate-metric
            [team]="filterForm.get('team')?.value"
            [startDate]="actualStartDate"
            [endDate]="actualEndDate"
            [isOtherMetricLoading]="isPrTimeMetricCurrentlyLoading"
            [org]="defaultOrgName"
            [project]="defaultProjectName">
          </app-change-fail-rate-metric>
    
          <app-deployment-frequency-metric
            [team]="filterForm.get('team')?.value"
            [startDate]="actualStartDate"
            [endDate]="actualEndDate"
            [isOtherMetricLoading]="isPrTimeMetricCurrentlyLoading"
            [org]="defaultOrgName"
            [project]="defaultProjectName">
          </app-deployment-frequency-metric>
    
          <app-pr-time-to-review-metric
            [team]="filterForm.get('team')?.value"
            [startDate]="actualStartDate"
            [endDate]="actualEndDate"
            [calculationLogic]="filterForm.get('calculationLogic')?.value"
            (prtLoadingState)="isPrTimeMetricCurrentlyLoading = $event"
            [org]="defaultOrgName"
            [project]="defaultProjectName">
          </app-pr-time-to-review-metric>
        </div>
    
        <app-pbi-time-by-column-metric
          class="pbi-card-full-width"
          [team]="filterForm.get('team')?.value"
          [startDate]="actualStartDate"
          [endDate]="actualEndDate"
          [isOtherMetricLoading]="isPrTimeMetricCurrentlyLoading"
          [org]="defaultOrgName"
          [project]="defaultProjectName">
        </app-pbi-time-by-column-metric>
      </div>
    </div>
    `,
  styles: [`
    .metrics-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 1100px;
      margin: 0 auto;
    }
    .metricsHeader {
      font: 750 2vw/4vw Roboto, Helvetica Neue, Helvetica, Arial, sans-serif;
      font-weight: bold;
      color: #0093a1;
      text-align: center;
      margin: 10px 10px 5px 10px;
    }
    .date-display-header {
      font-size: 1.7rem;
      font-weight: 600;
      color: #505050;
      text-align: center;
      margin-top: 0;
      margin-bottom: 18px;
    }
    .filter-bar {
      display: flex;
      justify-content: center;
      align-items: center; 
      padding: 20px;
      background: white;
      border-radius: 8px;
      width: 100%;
      box-sizing: border-box;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border-left: 4px solid #0093a1;
      border-right: 4px solid #0093a1;
    }
    .filter-controls {
      display: flex;
      gap: 16px;
      align-items: center;
    }
    mat-form-field {
      margin-right: 10px;
    }
    .metrics-cards-grid {
      display: grid;
      gap: 20px;
      width: 100%;
      margin-top: 20px;
      grid-auto-rows: min-content;
    }
    .kpi-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      align-items: stretch;
    }
    .pbi-card-full-width {
       min-height: 180px;
    }
  `]
})
export class TeamMetricsComponent implements OnInit, OnDestroy {
  filterForm!: FormGroup;
  teams$: Observable<string[]> = of([]);

  timeRangeOptions: TimeRangeOption[] = [
    { value: '7d', display: 'Last 7 days' },
    { value: '30d', display: 'Last 30 days' },
    { value: '90d', display: 'Last 90 days' },
    { value: '365d', display: 'Last 365 days' },
    { value: 'custom', display: 'Custom Range' }
  ];
  actualStartDate!: Date;
  actualEndDate!: Date;
  maxDate: Date;

  userProfile!: AdoUserProfile;
  availableAdoAccounts: AdoAccount[] = [];
  gitHubOrg?: string = '';
  defaultOrg?: AdoAccount;
  defaultOrgName: string = '';
  defaultProjectName: string = '';
  defaultProjectId: string = '';
  allProjects: any[] = [];
  
  environment: Environment;
  private readonly _destroying$ = new Subject<void>();

  currentDateRangeDisplay: string = '------------------------------';
  calculationLogicOptions: CalculationLogic[] = ['Median', 'Mean', 'IQR'];
  isPrTimeMetricCurrentlyLoading: boolean = false;


  /**
   * Constructs the TeamMetricsComponent.
   * @param fb FormBuilder for creating reactive forms.
   * @param userProfileService Service for user profile information.
   * @param environmentService Service for environment-specific configurations.
   * @param teamMetricsService Service for fetching team metrics data (used for bulk release data).
   */
  constructor(
    private readonly fb: FormBuilder,
    private readonly userProfileService: UserProfileService,
    private readonly environmentService: EnvironmentService,
    private readonly teamMetricsService: TeamMetricsService 
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.gitHubOrg = this.environment.github.org;
    this.maxDate = new Date();
  }

  /**
   * Angular lifecycle hook that initializes the component.
   * It performs the following actions in sequence:
   * 1. Initializes the filter form (`initializeForm`).
   * 2. Orchestrates the loading of initial data such as user profile and team context (`initializeFiltersAndData`).
   * 3. Sets up subscriptions to the filter form's value changes to update date ranges (`setupFormSubscriptions`).
   */
  ngOnInit(): void {
    this.filterForm = this.fb.group({
      timeRange: [this.timeRangeOptions[1].value],
      customStartDate: [null],
      customEndDate: [null],
      team: [null],
      calculationLogic: [DEFAULT_CALCULATION_LOGIC]
    });
    this.initializeFiltersAndData();
    this.setupFormSubscriptions();
  }

  /**
   * Sets up subscriptions to the `filterForm.valueChanges` observable.
   * This method configures a reactive pipeline to:
   * - Track previous and current form values.
   * - Filter events to act only on meaningful changes.
   * - Debounce input.
   * - Update the `actualStartDate` and `actualEndDate` if time range or custom dates change.
   * - Ensure dates are valid before allowing the stream to proceed (though no further action is taken in the subscribe block).
   * - Mark the form as pristine after changes.
   */
  setupFormSubscriptions(): void {
    this.filterForm.valueChanges.pipe(
      takeUntil(this._destroying$),
      startWith(this.filterForm.value),
      pairwise(),
      filter(([prev, curr]) => {
        const mainFiltersChanged = prev.timeRange !== curr.timeRange ||
                                   prev.team !== curr.team ||
                                   (curr.timeRange === 'custom' && 
                                    (prev.customStartDate !== curr.customStartDate || prev.customEndDate !== curr.customEndDate));
        return this.filterForm.dirty || mainFiltersChanged || prev.calculationLogic !== curr.calculationLogic;
      }),
      debounceTime(50),
      tap(([prev, curr]) => {
        if (prev.timeRange !== curr.timeRange || 
            (curr.timeRange === 'custom' && (prev.customStartDate !== curr.customStartDate || prev.customEndDate !== curr.customEndDate))) {
          this.calculateAndSetDateRange(curr.timeRange);
        }
      }),
      filter(() => this.areDatesValid(this.actualStartDate, this.actualEndDate))
    ).subscribe({
      next: () => {
        this.filterForm.markAsPristine({ emitEvent: false });
      },
      error: (err) => {
        console.error('Error in filterForm.valueChanges subscription:', err);
        this.filterForm.markAsPristine();
      }
    });
  }

  /**
   * Loads available teams from `TeamOwnerMinor` and the current user's ADO teams.
   * It then attempts to set a default team in the filter form.
   * @returns An Observable that completes once the default team selection process is finished or an error occurs.
   */
  private loadAndSetDefaultTeam(): Observable<void> {
    const availableTeamsObs$: Observable<string[]> = this.userProfileService.getTeamOwnerMinor(this.defaultOrg!.accountName).pipe(
      map((entries: TeamOwnerMinorEntry[]) => {
        if (!entries) return [];
        const teamNames = entries.map(entry => entry.team);
        return [...new Set(teamNames)].sort((a, b) => a.localeCompare(b));
      }),
      catchError(() => of([])),
      takeUntil(this._destroying$)
    );
    this.teams$ = availableTeamsObs$;

    const userAdoTeamsObs$: Observable<string[]> = (this.gitHubOrg && this.userProfile?.emailAddress)
      ? this.userProfileService.getUserFullMembership(this.gitHubOrg, this.userProfile.emailAddress).pipe(
          map(membership => membership.teams || []),
          catchError(() => of([]))
        )
      : of([]);

    return combineLatest([userAdoTeamsObs$, availableTeamsObs$]).pipe(
      take(1),
      tap(([userAdoTeams, availableOwnerTeams]) => {
        let defaultTeamSet = false;
        if (userAdoTeams && userAdoTeams.length > 0 && availableOwnerTeams && availableOwnerTeams.length > 0) {
          for (const adoTeamFullName of userAdoTeams) {
            const simpleAdoTeamName = adoTeamFullName.includes('\\') ? adoTeamFullName.substring(adoTeamFullName.lastIndexOf('\\') + 1) : adoTeamFullName;
            const matchingOwnerTeam = availableOwnerTeams.find((ot: string) => ot.toLowerCase() === simpleAdoTeamName.toLowerCase());
            if (matchingOwnerTeam) {
              this.filterForm.get('team')?.setValue(matchingOwnerTeam);
              defaultTeamSet = true;
              break;
            }
          }
        }
        if (!defaultTeamSet && availableOwnerTeams && availableOwnerTeams.length > 0) {
          this.filterForm.get('team')?.setValue(availableOwnerTeams[0]);
        }
      }),
      map(() => undefined),
      catchError(err => {
        console.error('Error in loadAndSetDefaultTeam:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Calls the service to initialize or retrieve cached bulk release data for the last 90 days.
   * @returns An Observable that completes when the bulk data initialization attempt is done.
   */
  retrieveBulkReleases(): Observable<any> {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    ninetyDaysAgo.setHours(0, 0, 0, 0);

    const now = new Date();
    now.setHours(23, 59, 59, 999);

    return this.teamMetricsService.initializeBulkReleaseData(ninetyDaysAgo.toISOString(), now.toISOString()).pipe(
      catchError(err => {
        console.error('Error initializing bulk release data:', err);
        return of(null);
      })
    );
  }

  /**
   * Orchestrates the complete initial data loading and setup sequence for the component.
   * This sequence includes:
   * 1. Retrieving bulk release data.
   * 2. Loading the current user's ADO profile.
   * 3. Loading ADO accounts available to the user.
   * 4. Setting a default ADO organization.
   * 5. Loading available teams and setting a default team.
   * 6. Calculating the initial date range for filters.
   */
  initializeFiltersAndData(): void {
    this.retrieveBulkReleases().pipe(
      switchMap(() => this.loadUserProfile()),
      switchMap(() => this.loadUserAccounts()),
      tap(() => this.setDefaultOrg()),
      switchMap(() => this.loadProjectsForAccount()),
      tap(() => this.setDefaultProject()),
      switchMap(() => this.loadAndSetDefaultTeam()),
      tap(() => this.calculateAndSetDateRange(this.filterForm.get('timeRange')?.value)),
      takeUntil(this._destroying$)
    ).subscribe({
      next: () => {
        this.filterForm.markAsPristine({ emitEvent: false });
      },
      error: (err) => {
        console.error('Error during initial data load sequence:', err);
        this.filterForm.markAsPristine();
      }
    });
  }

  /**
   * Loads the current user's profile information.
   * @returns An Observable of the ADO user profile.
   */
  private loadUserProfile(): Observable<AdoUserProfile> {
    return this.userProfileService.getCurrentProfile().pipe(
      map(profile => {
        if (!profile) {
          throw new Error('User profile not found');
        }
        this.userProfile = profile;
        return profile;
      })
    );
  }

  /**
   * Loads the ADO accounts available to the current user.
   * @returns An Observable of an array of ADO accounts.
   */
  private loadUserAccounts(): Observable<any> {
    if (!this.userProfile?.publicAlias) {
      return throwError(() => new Error('Public alias not found on profile for loading accounts'));
    }
    return this.userProfileService.getAccounts(this.userProfile.publicAlias).pipe(
      map(response => {
        this.availableAdoAccounts = response.value;
        return response.value;
      }),
      catchError(error => {
        console.error('Error loading user accounts:', error);
        return throwError(() => new Error('Failed to load user accounts'));
      })
    );
  }

  /**
   * Sets the default ADO organization based on environment configuration and available accounts.
   */
  private setDefaultOrg(): void {
    if (!this.availableAdoAccounts || this.availableAdoAccounts.length === 0) return;
    const defaultOrgNameFromEnv = this.environment.defaultOrg.toLowerCase();
    this.defaultOrg = this.availableAdoAccounts.find(
      acc => acc.accountName.toLowerCase() === defaultOrgNameFromEnv
    );
    this.defaultOrg ??= this.availableAdoAccounts.length > 0 ? this.availableAdoAccounts[0] : undefined;

    if (this.defaultOrg) {
      this.defaultOrgName = this.defaultOrg.accountName;
    }
  }

  /**
   * Loads all projects for the default ADO account.
   * @returns An Observable that completes when projects are loaded.
   */
  private loadProjectsForAccount(): Observable<any> {
    if (!this.defaultOrg) {
      return throwError(() => new Error('Default organization not set'));
    }
    return this.userProfileService.getProjects(this.defaultOrg.accountName).pipe(
      tap(projects => {
        this.allProjects = projects.value;
      }),
      catchError(err => {
        console.error('Error loading projects for account:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Sets the default project based on the environment and available projects.
   */
  private setDefaultProject(): void {
    if (!this.allProjects || this.allProjects.length === 0) {
      this.defaultProjectName = '';
      this.defaultProjectId = '';
      return;
    }

    const defaultProjectFromEnv = this.environment.defaultProject.toLowerCase();
    const matchingProject = this.allProjects.find(
      p => p.name.toLowerCase() === defaultProjectFromEnv
    );

    if (matchingProject) {
      this.defaultProjectName = matchingProject.name;
      this.defaultProjectId = matchingProject.id;
    } else {
      // Fallback or error handling if default project not found
      this.defaultProjectName = '';
      this.defaultProjectId = '';
    }
  }

  /**
   * Checks if the provided start and end dates are valid.
   * @param startDate The start date.
   * @param endDate The end date.
   * @returns True if both dates are non-null.
   */
  areDatesValid(startDate: Date | null, endDate: Date | null): boolean {
    return !!(startDate && endDate);
  }

  /**
   * Calculates and sets the component's `actualStartDate`, `actualEndDate`, and `currentDateRangeDisplay`
   * properties based on the selected time range from the form.
   * @param selectedValue The value of the selected time range option (e.g., '7d', '30d', 'custom').
   */
 calculateAndSetDateRange(selectedValue: string | null): void {
    if (!selectedValue) {
      this.actualStartDate = null as any;
      this.actualEndDate = null as any;
      return;
    }

    if (selectedValue === 'custom') {
      const pickerStartDate = this.filterForm.get('customStartDate')?.value;
      const pickerEndDate = this.filterForm.get('customEndDate')?.value;

      if (pickerStartDate && pickerEndDate) {
        this.actualStartDate = new Date(Date.UTC(pickerStartDate.getFullYear(), pickerStartDate.getMonth(), pickerStartDate.getDate(), 0, 0, 0, 0));
        const today = new Date();
        const isPickerEndDateToday = pickerEndDate.getFullYear() === today.getFullYear() && pickerEndDate.getMonth() === today.getMonth() && pickerEndDate.getDate() === today.getDate();
        if (isPickerEndDateToday) {
          const now = new Date();
          this.actualEndDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
        } else {
          this.actualEndDate = new Date(Date.UTC(pickerEndDate.getFullYear(), pickerEndDate.getMonth(), pickerEndDate.getDate(), 23, 59, 59, 999));
        }
      } else {
        this.actualStartDate = null as any;
        this.actualEndDate = null as any;
      }
    } else {
      this.filterForm.get('customStartDate')?.setValue(null, { emitEvent: false });
      this.filterForm.get('customEndDate')?.setValue(null, { emitEvent: false });
      
      const now = new Date();
      this.actualEndDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
      const normalizedEndDayStartUtc = new Date(Date.UTC(this.actualEndDate.getUTCFullYear(), this.actualEndDate.getUTCMonth(), this.actualEndDate.getUTCDate(), 0, 0, 0, 0));
      this.actualStartDate = new Date(normalizedEndDayStartUtc);
      switch (selectedValue) {
        case '7d': this.actualStartDate.setUTCDate(this.actualStartDate.getUTCDate() - 6); break;
        case '30d': this.actualStartDate.setUTCDate(this.actualStartDate.getUTCDate() - 29); break;
        case '90d': this.actualStartDate.setUTCDate(this.actualStartDate.getUTCDate() - 89); break;
        case '365d': this.actualStartDate.setUTCDate(this.actualStartDate.getUTCDate() - 364); break;
        default: this.actualStartDate.setUTCDate(this.actualStartDate.getUTCDate() - 6); break;
      }
    }
    
    this.formatDateRangeForFilterBar();
  }

  /**
   * Formats the `actualStartDate` and `actualEndDate` for display in the filter bar.
   * Sets `this.currentDateRangeDisplay` to a string like "M/D/YYYY - M/D/YYYY" using UTC dates.
   */
  formatDateRangeForFilterBar(): void {
    if (!this.actualStartDate || !this.actualEndDate) {
      return;
    }
    
    const startMonth = this.actualStartDate.getUTCMonth() + 1;
    const startDay = this.actualStartDate.getUTCDate();
    const startYear = this.actualStartDate.getUTCFullYear();
    
    const endMonth = this.actualEndDate.getUTCMonth() + 1;
    const endDay = this.actualEndDate.getUTCDate();
    const endYear = this.actualEndDate.getUTCFullYear();
    
    this.currentDateRangeDisplay = `${startMonth}/${startDay}/${startYear} - ${endMonth}/${endDay}/${endYear}`;
  }

  /**
   * Cleans up subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
